"use client";

import { useCallback, useEffect, useState } from "react";
import { usePathname, useSearchParams } from "next/navigation";
import { getStorageJSON } from "@/lib/client-storage";

const GA_ID = process.env.NEXT_PUBLIC_GA_ID || "";

declare global {
  interface Window {
    gtag?: (...args: unknown[]) => void;
    dataLayer?: unknown[];
  }
}

interface CookiePrefs {
  analytics: boolean;
}

function getStoredConsent(): boolean {
  const prefs = getStorageJSON<CookiePrefs>("cookie-consent");
  return prefs?.analytics ?? false;
}

export function Analytics() {
  const [consented, setConsented] = useState(() => getStoredConsent());
  const pathname = usePathname();
  const searchParams = useSearchParams();

  const handleStorageChange = useCallback(() => {
    setConsented(getStoredConsent());
  }, []);

  useEffect(() => {
    // "storage" fires only in OTHER tabs; "cookie-consent-change" is
    // dispatched by CookieConsent for same-tab accept/reject (the storage
    // event never fires in the tab that made the change).
    window.addEventListener("storage", handleStorageChange);
    window.addEventListener("cookie-consent-change", handleStorageChange);
    return () => {
      window.removeEventListener("storage", handleStorageChange);
      window.removeEventListener("cookie-consent-change", handleStorageChange);
    };
  }, [handleStorageChange]);

  useEffect(() => {
    // Basic consent mode
    // (https://developers.google.com/tag-platform/security/concepts/consent-mode):
    // no request to Google until the user grants analytics consent — the
    // gtag.js library itself is only fetched after opt-in, so no IP or
    // fingerprint reaches Google pre-consent.
    if (!GA_ID || !consented) return;
    if (document.querySelector(`script[src*="googletagmanager.com/gtag/js?id=${GA_ID}"]`)) return;
    const script = document.createElement("script");
    script.src = `https://www.googletagmanager.com/gtag/js?id=${GA_ID}`;
    script.async = true;
    document.head.appendChild(script);
  }, [consented]);

  useEffect(() => {
    // GA4 SPA pattern: public/analytics-init.js (generated at build time)
    // calls gtag('config', ID, {send_page_view:false}), so the automatic
    // page_view is suppressed and this manual config-with-page_path is the
    // single pageview source — no double counting on App Router client
    // navigations (see https://nextjs.org/docs/app/guides/third-party-libraries).
    // anonymize_ip is omitted: UA-era parameter, ignored by GA4.
    if (!GA_ID || !consented || !window.gtag) return;
    const url = pathname + (searchParams?.toString() ? `?${searchParams.toString()}` : "");
    window.gtag("config", GA_ID, {
      page_path: url,
      allow_google_signals: false,
      allow_ad_personalization_signals: false,
    });
  }, [consented, pathname, searchParams]);

  if (!GA_ID) return null;

  return null;
}
